import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Plus, CreditCard, Wallet, Pencil, Trash2, X } from 'lucide-react';

const EMPTY_FORM = { name: '', type: 'card', initial_balance: '' };

export function Accounts() {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const fetchAccounts = () => {
        setLoading(true);
        api.getAccounts()
            .then(setAccounts)
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...form,
            initial_balance: parseFloat(form.initial_balance) || 0,
        };

        const promise = editingId
            ? api.updateAccount(editingId, payload)
            : api.createAccount(payload);

        promise.then(() => {
            resetForm();
            fetchAccounts();
        });
    };

    const handleEdit = (account) => {
        setEditingId(account.id);
        setForm({
            name: account.name,
            type: account.type,
            initial_balance: account.initial_balance,
        });
        setShowForm(true);
    };

    const handleDelete = (id) => {
        if (!confirm('Sei sicuro di voler eliminare questo conto? I movimenti associati potrebbero rimanere orfani.')) {
            return;
        }
        api.deleteAccount(id).then(fetchAccounts);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Conti</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowForm(!showForm);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition"
                >
                    <Plus size={16} />
                    Nuovo conto
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-gray-900">
                            {editingId ? 'Modifica conto' : 'Nuovo conto'}
                        </h3>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            >
                                <option value="card">Carta</option>
                                <option value="cash">Contanti</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Saldo iniziale</label>
                            <input
                                type="number"
                                step="0.01"
                                value={form.initial_balance}
                                onChange={(e) => setForm({ ...form, initial_balance: e.target.value })}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            />
                        </div>
                        <div className="flex items-end">
                            <button type="submit" className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition">
                                {editingId ? 'Aggiorna' : 'Salva'}
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {loading ? (
                <p className="text-gray-500">Caricamento...</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {accounts.map((account) => {
                        const Icon = account.type === 'cash' ? Wallet : CreditCard;
                        return (
                            <div key={account.id} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                                            <Icon size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-900">{account.name}</h3>
                                            <p className="text-xs text-gray-500 capitalize">{account.type === 'cash' ? 'Contanti' : 'Carta'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleEdit(account)}
                                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                                            title="Modifica"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(account.id)}
                                            className="p-2 text-gray-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                            title="Elimina"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                                <p className="text-2xl font-bold text-gray-900">€ {parseFloat(account.balance).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Saldo iniziale: € {parseFloat(account.initial_balance).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
